<template>
  <AutoComplete
    v-model.trim="input"
    :input-id="id"
    :disabled="disabled"
    :suggestions="suggestions"
    :dropdown="!disabled"
    class="gl-code-autocomplete"
    data-key="code"
    option-label="code"
    auto-option-focus
    complete-on-focus
    @clear="onClear"
    @complete="onComplete($event.query)"
    @option-select="onOptionSelect"
  >
    <template #option="{ option }: { option: GLCodeOption }">
      <span v-if="option.adhoc" class="adhoc">
        {{ t('common.addParam', { param: `'${option.code}'` }) }}
      </span>
      <template v-else>{{ option.code }}</template>
    </template>
  </AutoComplete>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { GLCode } from '@rygen/rygen-ui/model';

import AutoComplete, {
  AutoCompleteOptionSelectEvent,
} from 'primevue/autocomplete';

type GLCodeOption = Partial<GLCode> & { adhoc?: boolean };

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: GLCode,
    default: () => new GLCode(),
  },
  glCodes: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, glCodes, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: GLCode): void;
}>();

const { t } = useI18n();

const internalValue: Ref<GLCode> = ref(new GLCode(modelValue.value));
const input = ref(internalValue.value.code);
const suggestions: Ref<GLCodeOption[]> = ref([]);

const hasManualInputPermission = computed(() =>
  authorization.hasAuthority('glCode', 'manualInput'),
);

const onOptionSelect = ({ value }: AutoCompleteOptionSelectEvent) => {
  internalValue.value = new GLCode(value);
  input.value = internalValue.value.code;
};

const onComplete = (query: string) => {
  const normalizedSearch = lowerCase(query).trim();

  suggestions.value = glCodes.value.filter((glCode) => {
    if (!glCode.code) {
      return false;
    }

    const normalizedCode = lowerCase(glCode.code).trim();
    return normalizedCode.includes(normalizedSearch);
  });

  const isValidQuery = !!query && query !== modelValue.value.code;
  if (hasManualInputPermission.value && isValidQuery) {
    const exactMatch = glCodes.value.find((t) => t.code === query);
    if (!exactMatch) {
      const newCode = {
        code: query,
        description: t('glCodes.manuallyEdited'),
        manuallyEdited: true,
        adhoc: true,
      };
      suggestions.value = [newCode, ...suggestions.value];
    }
  }
};

const onClear = () => {
  onComplete('');
};

watch(
  modelValue,
  (newVal: GLCode) => {
    internalValue.value = new GLCode(newVal);
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newVal: GLCode) => {
    if (modelValue.value.code !== newVal.code) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>
